/**
 * The request-a-quote modal. It turns the products a customer selected in the catalog, search
 * results, cart or a shopping list into a quote on the Quotes API. It prices the lines through
 * the Prices API, fetches their delivery dates, takes a customer reference, line notes, a line PO
 * and "domestic requested" per line, and then sends one of two requests:
 * - Save Quote posts to `APIUrl.ALL_QUOTES`. It is offered unless a line has a `pricePerUom` of
 *   0 or an inactive or unavailable product, so also when a line came back with no price or the
 *   pricing call failed. It needs a quote name. On success the modal closes and `onSuccess`
 *   runs.
 * - Request Price posts to `APIUrl.PRICE_REQUEST`. It is offered instead when a line has a
 *   `pricePerUom` of 0 or is inactive or unavailable, and needs no quote name. The Quotes API
 *   stores it as a quote with status Price Request (`web-quotes-api`
 *   `application/src/mappers/quoteEcom.mapper.ts` →
 *   `mapQuotePriceRequestBodyRequestToQuoteEcomModel`). On success the modal shows a result
 *   panel with a link to the new quote.
 * Beside Request Price, Remove & Reprice drops the unpriced or inactive lines from the modal and
 * prices the rest, so the customer can save a quote for those.
 *
 * Position: mounted by `Catalog/CatalogItems.tsx`,
 * `Search/Helpers/SearchBulkActionMenu/SearchBulkActionMenu.tsx`,
 * `ShoppingCart/ShoppingCartTable/ShoppingCartTable.tsx` and
 * `ShoppingListDetails/Helpers/ShoppingListHeader.tsx`, which own `showQuoteModal`. This file
 * holds the state and sends the quote request; `QuoteModalDesktop.tsx` and
 * `QuoteModalMobile.tsx` render it, split by `<Media>` at the `lg` breakpoint. Pricing and
 * delivery-times requests are built in `QuoteHelper.ts`; the request body type is
 * `Models/CreateQuoteRequest.model.tsx` → `CreateQuoteRequest`.
 *
 * Rules a change must keep:
 * - "the customer is never shown a price the Prices or Quotes API didn't return"; "the APIs may
 *   return a $0 value for price in which case we always show "TBD" to the end user and never
 *   "$0"". Every price the modal shows comes from `pricingData`, the Prices API response
 *   (`ApiUrl.PRICING_SERVICE_V2` in `@ryerson/frontend.common`). `QuoteHelper.ts` →
 *   `priceOrTbd` renders a 0 as `content.tbd`, and a line with `pricePerUom` 0 moves the modal
 *   to the Request Price path.
 * - "Before any response arrives, we should not be showing a price or TBD at all. It is still
 *   loading, so our typical standard would be an em-dash as a placeholder. Once the API loads or
 *   fails to load, we should be switching to TBD or the value returned." `priceOrTbd` shows an
 *   em dash for a missing price; `QuoteHelper.ts`'s header says where that matches this rule.
 * - On a later reload of a price or total already shown, whatever starts it: "Switch to an em dash
 *   until it arrives."
 * - "an order or payment request is never sent twice for one click", and the rule covers quote
 *   and price requests too. Save Quote checks `quoteCreateSubmitted`; a price request relies on
 *   the renderer's Request Price button (see `handleOnClickSaveQuote`).
 * - Lines are told apart by `materialSku` plus `id`, not by position: Remove & Reprice shrinks
 *   `displayItems`, but the parent's selection stays as it was.
 * - Pricing and delivery-times lines carry `itemNumber`, the line's 1-based position in
 *   `selectedProducts`. The renderers (`QuoteHelper.ts` → `getPricingInformationForItem`) and
 *   `handleRemoveReprice` look a line's price up by its position in `quoteProductList` plus 1,
 *   which matches only while every selected product has a `productState` entry.
 * - Customer reference, line notes and line PO go to the ERP, which rejects some characters
 *   (`Helper/shoppingCartHelper.ts` → `isErpInvalid`). Nothing is posted while any of them, or
 *   the quote name, fails `validateErpRestrictedCharacters`.
 * - Closing the modal clears every field and result and aborts in-flight pricing and
 *   delivery-times requests.
 *
 * Known defects against these rules: the mobile Request Price button is not disabled after a
 * tap, so each tap sends another price request (see `QuoteModalMobile.tsx`'s header); a
 * missing price shows an em dash not only while loading but also after a failed pricing
 * call and for a line the response carried no price for (see `QuoteHelper.ts`'s header and the
 * pricing effect below); and a re-price keeps showing the previous response's figures while
 * the new request is on its way and after it fails (see the pricing effect below).
 *
 * Related: `unit-tests/QuoteModal/QuoteModal.spec.tsx` (this component, with both renderers
 * mocked); `QuoteDetails/Context/QuoteDetailsContext.tsx` (the page a saved or price-request
 * quote opens on).
 */
import React, { useEffect, useRef, useState } from "react";
import { Media } from "@ryerson/frontend.next";
import QuoteModalDesktop from "./QuoteModalDesktop";
import QuoteModalMobile from "./QuoteModalMobile";
import QuoteModalContent from "./QuoteModalContent";
import { useApplication } from "@ryerson/frontend.application";
import {
	cancelAllInFlightRequests,
	getCustomerPartNumber,
	getMaxLeadTimeDay,
	isAxiosError,
	MatchingProduct,
	MAX_FIELD_LENGTHS,
	Product,
	validateErpRestrictedCharacters,
} from "@ryerson/frontend.common";
import { CurrencyCode } from "@ryerson/frontend.common";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { useTheme } from "@ryerson/frontend.theme";
import { ShoppingCartPricingResponse } from "@ryerson/frontend.common";
import { IDeliveryTimesResponse, ILeadTimeDay } from "@ryerson/common.models";
import { getCustomerFromAccountNumber, dateToString } from "@components/Helper/common";
import {
	CreateQuoteRequest,
	CreateQuoteResponse,
	LineItem,
} from "@components/Models/CreateQuoteRequest.model";
import { ShippingType } from "@ryerson/frontend.common";
import {
	checkIsActiveItem,
	getDeliveryDateForQuoteItems,
	getPricingForQuoteItems,
	HasActiveAvailableProps,
	intoErpValidator,
	mapProcessingToRequestFormat,
	QuoteInput,
	QuoteModalProductType,
	QuoteModalProductTypeKeys,
	QuoteModalProps,
	updateDisplayItems,
} from "./QuoteHelper";
import { AjaxERPPricingResponse } from "@ryerson/frontend.common";
import { APIUrl } from "@enums/apiUrl.enums";
import { getDeliveryDateAndValidDateRange } from "@components/Helper/DeliveryDateAndMinMaxRange";
import { getZipCode } from "@components/AccountSettings/Helpers/zipCode";
import { UOM } from "@ryerson/common.enums";
import { AddressType } from "@enums/address.enum";
import {
	findInvalidErpInputIndex,
	hasErpInvalidInputs,
} from "@components/Helper/shoppingCartHelper";
import { AxiosError, AxiosResponse } from "axios";
import { IError } from "@ryerson/common.models";
import { getShippingPlant } from "@components/Helper/leadTimesHelpers";

/**
 * Outcome of the last quote request's post. `display` is true once a response (or failure) has
 * come back; `success` chooses between the result panel and the failure message. A Request
 * Price success sets `success` and `quote`, the created Price Request quote, whose `id` the
 * View Details button opens. A failed Save Quote also sets `{ display: true, success: false }`;
 * a Save Quote success leaves it unchanged. Reset to `{ display: false, success: false }` when
 * the modal closes.
 */
export type PriceRequestResult = {
	display: boolean;
	success: boolean;
	quote?: CreateQuoteResponse;
};

/**
 * A selected product still shown in the modal (not removed by Remove & Reprice).
 * `requestedDeliveryDate` is the requested date the selected product arrived with, when it has
 * one. It sets the line's `isFutureDeliveryDate`, and the request sends it as the line's date
 * when it falls inside the delivery-times range (`Helper/DeliveryDateAndMinMaxRange.ts` →
 * `getDeliveryDateAndValidDateRange`).
 */
export type DisplayItems = (Product | MatchingProduct) & {
	itemNumber: string | number;
	requestedDeliveryDate?: string;
};

/**
 * The request-a-quote modal (see the file header). The parent owns `showQuoteModal`; opening it
 * builds the quote lines from `selectedProducts` and `productState` and starts pricing.
 * `clearSelectedProducts` and `bulkShoppingList` are accepted but not read.
 */
const QuoteModal: React.FC<QuoteModalProps> = ({
	showQuoteModal,
	setShowQuoteModal,
	selectedProducts,
	clearSelectedProducts,
	productState,
	onSuccess,
	type,
	purchaseOrderNumber,
	canSelectCustomerTBDPart,
}) => {
	const {
		localization: { language },
		user: { isLoggedIn },
		zipCode,
		axiosInstance,
		profile,
	} = useApplication();
	const accentColor = useTheme().theme.colors.secondary.link;

	const {
		customerPickup,
		selectedShipToAddress,
		shippingAndBillingInformation,
		salesPlant,
		shipToAddresses,
	} = useEcommerce();

	/**
	 * Here, we can’t use `defaultShipToAddress` from e-commerce
	 * due to a bug in the e-commerce context. Currently, the `defaultShipToAddress` is being
	 * updated every time user selects a new address, but that shouldn’t be the case.
	 * Only the `selectedShipTo` should be updated.
	 */
	const defaultShipToAddress =
		shipToAddresses?.find((address) => address.default) || selectedShipToAddress;

	const content = QuoteModalContent(accentColor)[language || "en"];
	const [quoteName, setQuoteName] = useState<string>("");
	const [quoteProductList, setQuoteProductList] = useState<
		(QuoteModalProductType & { id: number | string })[]
	>([]);
	const [submitted, setSubmitted] = useState<boolean>(false);
	const [quoteCreateSubmitted, setQuoteCreateSubmitted] = useState<boolean>(false);
	const [pricingData, setPricingData] = useState<ShoppingCartPricingResponse>(
		{} as ShoppingCartPricingResponse
	);
	// omittedItems are quote lines that had no price or an inactive or unavailable product
	// when Remove & Reprice was pressed
	const [omittedItems, setOmittedItems] = useState<
		(QuoteModalProductType & { id: number | string })[]
	>([]);

	const modalPricingAbortControllers = useRef<AbortController[]>([]);
	const modalDeliveryTimesAbortControllers = useRef<AbortController[]>([]);

	// displayItems are items that were selected for quoting and were not removed
	// by the Remove & Reprice button. This distinction is significant because
	// omitting via R&R button should not unselect selectedItems (in the parent
	// context, e.g. Catalog, Cart, etc.), but omittedItems should not be priced.
	const [displayItems, setDisplayItems] = useState<Array<DisplayItems>>([]);
	const [deliveryTimes, setDeliveryTimes] = useState<IDeliveryTimesResponse[]>([]);
	const quoteProductListRef = useRef<(QuoteModalProductType & { id: number | string })[]>([]);
	/** Does the current submitted checkout contain invalid chars in the inputs? */
	const [invalidCheckoutSubmitted, setInvalidCheckoutSubmitted] = useState<boolean>(false);
	/**
	 * Use this ref to bring invalid inputs into view when trying to submit (see the
	 * `invalidCheckoutSubmitted` effect).
	 */
	const validationInputRef = useRef<HTMLElement>(); // the renderers attach it to the first row

	const [priceRequestResult, setPriceRequestResult] = useState<PriceRequestResult>({
		display: false,
		success: false,
	});
	const [isRetrievingPricing, setIsRetrievingPricing] = useState<boolean>(false);

	useEffect(() => {
		if (quoteProductList.length > 0 && showQuoteModal) {
			updateDisplayItems(selectedProducts, omittedItems, setDisplayItems);
		}
	}, [omittedItems, selectedProducts, showQuoteModal]);

	// Every open and close drops the previous pricing; a close also clears the form and the
	// price-request result and aborts the requests still in flight.
	useEffect(() => {
		setPricingData({} as ShoppingCartPricingResponse);
		setDeliveryTimes([]);

		if (!showQuoteModal) {
			setOmittedItems([]);
			setDisplayItems([]);
			setQuoteName("");
			setSubmitted(false);
			setQuoteCreateSubmitted(false);
			setPriceRequestResult({ display: false, success: false });

			modalPricingAbortControllers.current.length &&
				cancelAllInFlightRequests(modalPricingAbortControllers.current);
			setIsRetrievingPricing(false);
			modalDeliveryTimesAbortControllers.current.length &&
				cancelAllInFlightRequests(modalDeliveryTimesAbortControllers.current);
		}
	}, [showQuoteModal]);

	useEffect(() => {
		quoteProductListRef.current = quoteProductList;
	}, [quoteProductList]);

	// Prices and dates the shown lines whenever `displayItems` changes. A change that arrives
	// while a pricing call is in flight is skipped, not queued: this effect does not re-run when
	// `isRetrievingPricing` clears. `getPricingOfProductsFromERP` calls neither callback for an
	// aborted request, so the close effect above clears the flag itself.
	// `pricingData` is cleared only on open and close. A re-price (Remove & Reprice, or a parent
	// re-render) therefore leaves the previous response in place: until the new response
	// arrives, the lines show its prices and the subtotal is worked out from it, where an em
	// dash belongs. When the re-price fails, both keep showing the previous response's figures.
	// Each of the two is a known defect against the loading rules in the file header.
	useEffect(() => {
		if (displayItems.length === 0 || isRetrievingPricing) {
			return;
		}
		setIsRetrievingPricing(true);

		getPricingForQuoteItems(
			axiosInstance,
			isLoggedIn,
			showQuoteModal,
			customerPickup,
			getZipCode(isLoggedIn, zipCode, selectedShipToAddress, customerPickup, salesPlant),
			displayItems,
			quoteProductList,
			customerPickup ? defaultShipToAddress : selectedShipToAddress,
			shippingAndBillingInformation,
			handleOnSuccessPricing,
			profile?.customer?.customerNumber,
			getShippingPlant(
				isLoggedIn,
				customerPickup,
				selectedShipToAddress,
				defaultShipToAddress
			),
			() => {
				setIsRetrievingPricing(false);
			},
			modalPricingAbortControllers
		);
		getDeliveryDateForQuoteItems(
			axiosInstance,
			isLoggedIn,
			showQuoteModal,
			customerPickup,
			zipCode,
			displayItems,
			quoteProductList,
			customerPickup ? defaultShipToAddress : selectedShipToAddress,
			salesPlant,
			handleOnSuccessDeliveryDateAndLeadTime,
			defaultShipToAddress,
			modalDeliveryTimesAbortControllers
		);
	}, [displayItems]);

	// Builds one quote line per selected product from its `productState` entry (quantity, UOMs,
	// cut, and the reference / notes / PO the parent already holds) and refreshes
	// `displayItems`, which starts pricing. A selected product with no matching entry gets no
	// line. The catalog, search and cart callers build `selectedProducts` with `.map()` while
	// rendering, so this runs again on each of their renders while the modal is open and, when
	// no pricing call is in flight, prices the lines again.
	useEffect(() => {
		if (showQuoteModal) {
			const updatedQuoteProductList: (QuoteModalProductType & { id: number | string })[] = [];
			selectedProducts.forEach((product, productIndex) => {
				let currentStateOfProduct = productState.find((currentState) => {
					return (
						currentState.materialMaster === product.materialMaster &&
						(() =>
							type !== "catalog" ? currentState.index === productIndex + 1 : true) &&
						/**
						 * Duplicate lines of one SKU are told apart by `id`. The arrow
						 * function above is never called, so it is always truthy and the
						 * `index` comparison inside it does not take part in the match.
						 */
						currentState.id === product.id
					);
				});

				if (currentStateOfProduct) {
					updatedQuoteProductList.push({
						id: product.id,
						altPartNumber: product.altPartNumber || "",
						comboKey: product.comboKey || "",
						customerRefNumber:
							"customerRefNumber" in currentStateOfProduct
								? currentStateOfProduct.customerRefNumber || ""
								: "",
						deliveryDate: "",
						domesticRequired:
							"domesticRequired" in currentStateOfProduct
								? currentStateOfProduct.domesticRequired || false
								: false,
						extendedPrice: "0",
						itemNumber: productIndex + 1,
						lineInstructions: [],
						lineItemNotes:
							"notes" in currentStateOfProduct
								? currentStateOfProduct.notes || ""
								: "",
						lineLevelPO:
							"purchaseOrderNumber" in currentStateOfProduct
								? currentStateOfProduct.purchaseOrderNumber || ""
								: "",
						materialSku: product.materialMaster,
						partNumber: product.altPartNumber || "",
						positionId: "positionId" in product ? product.positionId : 0,
						price: 0,
						pricingUom: currentStateOfProduct.priceUom as UOM,
						processing:
							"processing" in currentStateOfProduct
								? currentStateOfProduct.processing
								: {},
						purchaseOrderNumber: "",
						quantity: currentStateOfProduct.quantity,
						quantityUom: currentStateOfProduct.qtyUom as UOM,
						sourcePlant: "",
					});
				}
			});
			quoteProductListRef.current = [...updatedQuoteProductList];
			setQuoteProductList([...updatedQuoteProductList]);
			updateDisplayItems(selectedProducts, omittedItems, setDisplayItems);
		}
	}, [showQuoteModal, selectedProducts, productState]);

	const handleOnSuccessPricing = (responseData: AjaxERPPricingResponse) => {
		setIsRetrievingPricing(false);
		if (responseData?.data) {
			setPricingData(responseData?.data);
		}
	};

	const handleOnSuccessDeliveryDateAndLeadTime = (responseData: {
		data: IDeliveryTimesResponse[];
	}) => {
		if (responseData?.data) {
			setDeliveryTimes(responseData?.data);
		}
	};

	/**
	 * Writes one input of one quote line (matched by SKU and `itemNumber`) into both
	 * `quoteProductListRef` and `quoteProductList`. The renderers and the submit path's ERP check
	 * and line mapping read the ref; `invalidQuoteInputs`, `handleRemoveReprice` and the pricing
	 * effect read the state, and the state update is what re-renders the renderers, so the two
	 * must change together. The line object is updated in place before the array is copied.
	 */
	const handleOnChange = (
		materialSku: string,
		itemNumber: number,
		option: QuoteModalProductTypeKeys,
		value: string | boolean
	) => {
		let newQuoteProductList = quoteProductListRef.current;
		let currentIndex = newQuoteProductList.findIndex(
			(item: QuoteModalProductType) =>
				item.materialSku === materialSku && item.itemNumber === itemNumber
		);

		if (currentIndex > -1) {
			let currentQuoteProductList = newQuoteProductList[currentIndex];
			// this awkward block is to appease the type checker
			if (
				typeof value === "boolean" &&
				option === QuoteModalProductTypeKeys.domesticRequired
			) {
				currentQuoteProductList[option] = value;
			} else if (
				typeof value === "string" &&
				(option === QuoteModalProductTypeKeys.customerRefNumber ||
					option === QuoteModalProductTypeKeys.lineItemNotes ||
					option === QuoteModalProductTypeKeys.lineLevelPO)
			) {
				currentQuoteProductList[option] = value;
			}
			newQuoteProductList[currentIndex] = currentQuoteProductList;
			quoteProductListRef.current = [...newQuoteProductList];
			setQuoteProductList([...newQuoteProductList]);
		}
	};

	/**
	 * If form was submitted with invalid inputs, scroll these into view.
	 * `validationInputRef` is whichever input the renderers attached it to; both attach it to a
	 * row picked by `quoteProductList.findIndex(intoErpValidator)`, which is always the first
	 * row. Clearing `submitted` afterwards also drops the quote-name field's error state.
	 */
	useEffect(() => {
		if (invalidCheckoutSubmitted) {
			const idx = findInvalidErpInputIndex(quoteProductListRef.current.map(intoErpValidator));
			if (idx > -1) {
				validationInputRef?.current?.scrollIntoView();
				setInvalidCheckoutSubmitted(false);
				setSubmitted(false);
			}
		}
	}, [invalidCheckoutSubmitted]);

	/**
	 * Builds the quote request from the shown lines and posts it: to `APIUrl.ALL_QUOTES` for Save
	 * Quote, or to `APIUrl.PRICE_REQUEST` when `isPriceRequest` is true (Request Price).
	 *
	 * Nothing is sent while a line's reference, notes or PO, or the quote name, holds an
	 * ERP-restricted character. Save Quote also needs a quote name and is refused while
	 * `quoteCreateSubmitted` is set; a price request checks neither. Only the desktop Request
	 * Price button (`QuoteModalDesktop.tsx` → `ButtonArea`) blocks a second send; the mobile one
	 * does not (see `QuoteModalMobile.tsx`'s header), a known defect against the single-send rule
	 * in the file header.
	 *
	 * Each line takes its price, UOMs and quantity from the Prices API response and its date from
	 * its latest delivery-times lead day (`getMaxLeadTimeDay`, then
	 * `getDeliveryDateAndValidDateRange`). `deliveryDate` is declared outside the map, so a line
	 * with no lead days reuses the previous line's.
	 *
	 * A Save Quote success closes the modal and calls `onSuccess`; a price-request success stores
	 * the created quote in `priceRequestResult`. A non-200 or failed call, of either kind, sets
	 * `priceRequestResult` to a failure, which both renderers show as the "something went wrong"
	 * message. When there are lines but `pricingData` has no `items` (pricing failed or has not
	 * returned), the line mapping throws, the outer `catch` swallows it, and nothing is sent or
	 * shown.
	 */
	const handleOnClickSaveQuote = async (isPriceRequest: boolean = false) => {
		const invalidCO = hasErpInvalidInputs(quoteProductListRef.current.map(intoErpValidator));
		setInvalidCheckoutSubmitted(invalidCO);

		setSubmitted(true);
		if (
			(!isPriceRequest ? quoteName && !quoteCreateSubmitted : true) &&
			!invalidCO &&
			!validateErpRestrictedCharacters(quoteName).length &&
			!invalidQuoteInputs()
		) {
			try {
				setQuoteCreateSubmitted(true);

				let deliveryDate = {} as ILeadTimeDay;
				let lineItems: Array<LineItem> = displayItems.map((displayItem, index: number) => {
					/**
					 * We can’t identify items using indices here.
					 * That’s because {@link displayItems} isn’t a fixed-sized list.
					 * Its indices and items change when the user hits `remove & reprice`.
					 * Let’s use ids instead.
					 */
					let item = quoteProductListRef.current.find((item) => {
						const found =
							item.materialSku === displayItem.materialMaster &&
							item.id === displayItem.id;
						return found;
					});

					const itemPriceData = pricingData.items.find(
						(priceItem) =>
							priceItem.materialSku === item?.materialSku &&
							priceItem.itemNumber === item?.itemNumber
					);

					const leadTimeDays =
						deliveryTimes.find((time) => {
							return (
								time.materialSku === item?.materialSku &&
								time.itemNumber === item.itemNumber
							);
						})?.leadTimeDays || [];

					if (leadTimeDays && leadTimeDays?.length > 0) {
						deliveryDate = getMaxLeadTimeDay(leadTimeDays);
					}
					const { startDate } = getDeliveryDateAndValidDateRange(
						isLoggedIn,
						false,
						deliveryDate,
						displayItem.requestedDeliveryDate,
						canSelectCustomerTBDPart
					);

					const extendedPrice = Number(itemPriceData?.extendedPrice);
					return {
						comboKey: item?.comboKey || "",
						contractLineNumber: "",
						contractNumber: "",
						customerPartNumber: getCustomerPartNumber(item?.altPartNumber || ""),
						customerPurchaseOrderLineNumber: "",
						customerReferenceNumber: item?.customerRefNumber,
						domesticRequired: item?.domesticRequired || false,
						extendedPrice: extendedPrice,
						itemNumber: item?.itemNumber || index + 1,
						notes: item?.lineItemNotes || "",
						price: itemPriceData?.pricePerUom,
						pricingUom: itemPriceData?.pricingUom,
						purchaseOrderNumber: item?.lineLevelPO || "",
						quantity: Number(itemPriceData?.quantity || 0),
						quantityUom: itemPriceData?.quantityUom,
						requestedDeliveryDate: startDate ? dateToString(new Date(startDate)) : null,
						isFutureDeliveryDate: !!displayItem.requestedDeliveryDate,
						skuNumber: item?.materialSku,
						...(item?.processing &&
							Object.keys(item?.processing).length > 0 && {
								cutProcessing: mapProcessingToRequestFormat(item?.processing),
							}),
					};
				});

				const createQuoteRequestBody: CreateQuoteRequest = {
					shippingType: customerPickup
						? ShippingType.customerPickup
						: ShippingType.standard,
					quoteName: quoteName,
					purchaseOrderNumber:
						purchaseOrderNumber || profile?.settings?.purchaseOrderNumber || "",
					currency: CurrencyCode.us,
					newShipTo: false,
					salesRepId: "",
					transactionNumber: "",
					customerReferenceNumber: "",
					isResidentialAddress:
						selectedShipToAddress.addressType === AddressType.RESIDENTIAL
							? true
							: false,
					lineItems,
					zipCode: getZipCode(
						isLoggedIn,
						zipCode,
						selectedShipToAddress,
						customerPickup,
						salesPlant
					),
					partners: {
						soldTo: {
							partnerNumber: getCustomerFromAccountNumber(
								profile?.customer?.customerNumber
							)?.customerNumber,
							firstName: profile?.firstName,
							lastName: profile?.lastName,
							email: profile?.email,
						},
						shipTo: {
							partnerNumber: customerPickup
								? defaultShipToAddress?.shipToNumber
								: selectedShipToAddress?.shipToNumber,
						},
						billTo: {
							partnerNumber: getCustomerFromAccountNumber(
								profile?.customer?.customerNumber
							)?.customerNumber,
						},
					},
					enteredBy: {
						email: profile?.email,
						firstName: profile?.firstName,
						lastName: profile?.lastName,
					},
				};
				await axiosInstance
					.post<null, AxiosResponse<CreateQuoteResponse> | AxiosError<IError>>(
						!isPriceRequest ? APIUrl.ALL_QUOTES : APIUrl.PRICE_REQUEST,
						{
							...createQuoteRequestBody,
						}
					)
					.then((response) => {
						if (isAxiosError(response) || response.status !== 200) {
							throw response;
						}
						setQuoteCreateSubmitted(false);
						if (!isPriceRequest) {
							setShowQuoteModal(false);
							onSuccess && onSuccess();
						} else {
							setPriceRequestResult({
								display: true,
								success: true,
								quote: (response as AxiosResponse<CreateQuoteResponse>).data,
							});
						}
					})
					.catch(() => {
						setPriceRequestResult({ display: true, success: false });
						setQuoteCreateSubmitted(false);
					});
			} catch {
				setQuoteCreateSubmitted(false);
			}
		}
	};

	/**
	 * True when any line's reference, line PO or notes holds an ERP-restricted character. It
	 * covers `lineLevelPO`, which the `hasErpInvalidInputs` check above does not see:
	 * {@link intoErpValidator} passes that check the line's `purchaseOrderNumber`, which is always
	 * empty here. A bad line PO therefore blocks the request without scrolling to it.
	 */
	const invalidQuoteInputs = () => {
		return quoteProductList.some(
			(productList) =>
				validateErpRestrictedCharacters(productList.customerRefNumber).length ||
				validateErpRestrictedCharacters(productList.lineLevelPO).length ||
				validateErpRestrictedCharacters(productList.lineItemNotes).length
		);
	};

	/**
	 * Remove & Reprice: marks every line with no `pricePerUom` (0 or missing) or whose product is
	 * inactive or unavailable as omitted. The `omittedItems` effect then rebuilds `displayItems`
	 * without them, which prices the rest again. The set is recomputed from all of
	 * `quoteProductList` each time, so lines omitted earlier (no longer priced) stay omitted.
	 * `QuoteHelper.ts` → `updateDisplayItems` omits by SKU, so every line of an omitted SKU goes.
	 * The parent's selection is not changed.
	 */
	const handleRemoveReprice = () => {
		// lines with no price or an inactive or unavailable product

		const selectedProductsWithoutPrice = quoteProductList.filter((product, index) => {
			// checking pricing data for pricePerUom
			const itemPriceData = pricingData.items.find(
				(priceItem) =>
					priceItem.materialSku === product.materialSku &&
					priceItem.itemNumber === index + 1
			);

			// check for inactive items in displayItems, (inside modal called selectedProducts)
			const displayedItem = displayItems.find(
				(item) => item.materialMaster === product.materialSku
			);

			return (
				!itemPriceData?.pricePerUom ||
				!checkIsActiveItem(displayedItem as HasActiveAvailableProps)
			);
		});
		setOmittedItems([...selectedProductsWithoutPrice]);
	};

	// The per-line inputs both renderers draw, in display order. Text values are cut to the
	// shared ERP field lengths; the customer reference uses the header PO length.
	const quoteInputs: QuoteInput[] = [
		{
			label: content.customerRefNumber,
			productTypeKey: QuoteModalProductTypeKeys.customerRefNumber,
			onChange: (event: any, product: any) =>
				handleOnChange(
					product.materialSku,
					product.itemNumber,
					QuoteModalProductTypeKeys.customerRefNumber,
					event.target.value.substring(0, MAX_FIELD_LENGTHS.HEADER_PO)
				),
		},
		{
			label: content.lineItemNotes,
			productTypeKey: QuoteModalProductTypeKeys.lineItemNotes,
			onChange: (event: any, product: any) =>
				handleOnChange(
					product.materialSku,
					product.itemNumber,
					QuoteModalProductTypeKeys.lineItemNotes,
					event.target.value.substring(0, MAX_FIELD_LENGTHS.LINE_LEVEL_NOTES)
				),
		},
		{
			label: content.lineLevelPO,
			productTypeKey: QuoteModalProductTypeKeys.lineLevelPO,
			onChange: (event: any, product: any) =>
				handleOnChange(
					product.materialSku,
					product.itemNumber,
					QuoteModalProductTypeKeys.lineLevelPO,
					event.target.value.substring(0, MAX_FIELD_LENGTHS.LINE_LEVEL_PO)
				),
		},
		{
			type: "checkbox",
			label: content.domesticRequired,
			productTypeKey: QuoteModalProductTypeKeys.domesticRequired,
			onChange: (newVal: boolean, product: QuoteModalProductType) =>
				handleOnChange(
					product.materialSku,
					product.itemNumber,
					QuoteModalProductTypeKeys.domesticRequired,
					newVal
				),
		},
	];

	return (
		<>
			<Media lessThan="lg">
				<QuoteModalMobile
					quoteName={quoteName}
					setQuoteName={setQuoteName}
					showQuoteModal={showQuoteModal}
					setShowQuoteModal={setShowQuoteModal}
					selectedProducts={displayItems}
					quoteProductList={quoteProductListRef.current}
					content={content}
					pricingData={pricingData}
					handleOnClickSaveQuote={handleOnClickSaveQuote}
					handleSaveAsList={() => {
						handleOnClickSaveQuote(true);
					}}
					handleRemoveReprice={handleRemoveReprice}
					quoteInputs={quoteInputs}
					submitted={submitted}
					quoteCreateSubmitted={quoteCreateSubmitted}
					validationInputRef={validationInputRef}
					priceRequestResult={priceRequestResult}
					setPriceRequestResult={setPriceRequestResult}
					isRetrievingPricing={isRetrievingPricing}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<QuoteModalDesktop
					quoteName={quoteName}
					setQuoteName={setQuoteName}
					showQuoteModal={showQuoteModal}
					setShowQuoteModal={setShowQuoteModal}
					selectedProducts={displayItems}
					quoteProductList={quoteProductListRef.current}
					content={content}
					pricingData={pricingData}
					handleOnClickSaveQuote={handleOnClickSaveQuote}
					handleSaveAsList={() => {
						handleOnClickSaveQuote(true);
					}}
					handleRemoveReprice={handleRemoveReprice}
					quoteInputs={quoteInputs}
					submitted={submitted}
					quoteCreateSubmitted={quoteCreateSubmitted}
					validationInputRef={validationInputRef}
					priceRequestResult={priceRequestResult}
					setPriceRequestResult={setPriceRequestResult}
					isRetrievingPricing={isRetrievingPricing}
				/>
			</Media>
		</>
	);
};

export default QuoteModal;
